import { MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react'

import { FecImportLifecycleStatus } from '../enums/fecImportLifecycleStatus'
import type { FecImportItem } from '../query-api'

// What a failed import is shown as right after a retry was accepted, until the server says otherwise.
const RETRY_PENDING_STATUS: Partial<Record<FecImportLifecycleStatus, FecImportLifecycleStatus>> = {
  [FecImportLifecycleStatus.DetectionFailed]: FecImportLifecycleStatus.DetectingMappings,
  [FecImportLifecycleStatus.ParsingFailed]: FecImportLifecycleStatus.AwaitingProcessing,
}

/** A retried import as the user left it. The server has acted on the retry once its `updatedTime` moves on. */
export type RetriedFecImport = Pick<FecImportItem, 'id' | 'updatedTime'>

interface UseRetriedFecImportsParams {
  hasAnalysisTimedOut: boolean
  /** Kept in sync with "is any retry still unconfirmed by the server", so the list query can keep polling. */
  hasRetriedImportsRef: MutableRefObject<boolean>
  serverFecImports: FecImportItem[]
  sessionImportIds: string[]
}

interface UseRetriedFecImportsResult {
  /** The server list, with the imports that were just retried shown as running instead of failed. */
  fecImports: FecImportItem[]
  markImportsRetried: (imports: RetriedFecImport[]) => void
}

export const useRetriedFecImports = ({
  hasAnalysisTimedOut,
  hasRetriedImportsRef,
  serverFecImports,
  sessionImportIds,
}: UseRetriedFecImportsParams): UseRetriedFecImportsResult => {
  const [retriedImports, setRetriedImports] = useState<RetriedFecImport[]>([])

  useEffect(() => {
    hasRetriedImportsRef.current = retriedImports.length > 0
  }, [hasRetriedImportsRef, retriedImports.length])

  const fecImports = useMemo(
    () =>
      serverFecImports.map((fecImport) => {
        const pendingStatus = RETRY_PENDING_STATUS[fecImport.status]

        // Only while the import is in the session: once the session is reset or reuploaded the stale failure must
        // not look like a running import that gets adopted again.
        return pendingStatus &&
          sessionImportIds.includes(fecImport.id) &&
          retriedImports.some(
            (retriedImport) => retriedImport.id === fecImport.id && retriedImport.updatedTime === fecImport.updatedTime,
          )
          ? { ...fecImport, canRetry: false, errorMessage: null, mappingErrorMessage: null, status: pendingStatus }
          : fecImport
      }),
    [retriedImports, serverFecImports, sessionImportIds],
  )

  // A retried import stops being masked as soon as the server reports anything but the failure it had. A new failure
  // counts too: the status is the same again, but the import was updated.
  useEffect(() => {
    if (retriedImports.length === 0) {
      return
    }

    const stillFailedImports = retriedImports.filter(
      (retriedImport) =>
        !hasAnalysisTimedOut &&
        sessionImportIds.includes(retriedImport.id) &&
        serverFecImports.some(
          (fecImport) =>
            fecImport.id === retriedImport.id &&
            fecImport.updatedTime === retriedImport.updatedTime &&
            RETRY_PENDING_STATUS[fecImport.status] !== undefined,
        ),
    )

    if (stillFailedImports.length !== retriedImports.length) {
      setRetriedImports(stillFailedImports)
    }
  }, [hasAnalysisTimedOut, retriedImports, serverFecImports, sessionImportIds])

  const markImportsRetried = useCallback((imports: RetriedFecImport[]) => {
    setRetriedImports((currentImports) => [
      ...currentImports.filter((currentImport) => !imports.some(({ id }) => id === currentImport.id)),
      ...imports.map(({ id, updatedTime }) => ({ id, updatedTime })),
    ])
  }, [])

  return { fecImports, markImportsRetried }
}
